// A tiny external store holding the pathname the nav chrome should treat as "active" WHILE a transition is
// animating. The router commits the URL at the start of a slide, but the active-link highlight should follow
// the motion and flip at the animation's midpoint — so the controller drives this store (origin before the
// midpoint, destination after) and the header reads it instead of the raw location. `null` when idle → the
// header falls back to the real current pathname.

import { useSyncExternalStore } from 'react'
import { gtlog } from './debug'

let current: string | null = null
const listeners = new Set<() => void>()

export const navVisualPath = {
  set(value: string | null): void {
    if (value === current) return
    // A null (or destination) write while a slide is mid-flight is the "active tab flashes to the target"
    // bug: the chrome falls back to the committed location for a frame, then the next publish snaps it
    // back. Record who did it — this fires rarely, only on suspicious writes, and made the culprit findable.
    if (import.meta.env.DEV) {
      const running = progress
      if (running && running.progress > 0.02 && running.progress < 0.98 && value !== current) {
        const suspicious = value === null || (value === running.to && running.progress < 0.45)
        if (suspicious) {
          gtlog('VISUAL-PATH-SUSPECT', {
            value,
            at: Math.round(running.progress * 100) / 100,
            stack: new Error('trace').stack?.split('\n').slice(2, 6).join(' | '),
          })
        }
      }
    }
    current = value
    for (const l of listeners) l()
  },
  get: (): string | null => current,
  // An arrow, not a method: it is handed to useSyncExternalStore as a bare reference (below), so it must not
  // depend on being called as `navVisualPath.subscribe(...)`.
  subscribe: (l: () => void): (() => void) => {
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  },
}

/** The transition-overridden active pathname, or `null` when idle (caller uses the real location). */
export function useNavVisualPath(): string | null {
  return useSyncExternalStore(
    navVisualPath.subscribe,
    () => navVisualPath.get(),
    () => null, // SSR: no transition in flight, header uses the real pathname
  )
}

/** Where a running slide currently is, between two members of a group. */
export interface TransitionProgress {
  from: string
  to: string
  /** 0 = fully on `from`, 1 = fully on `to`. Follows the finger exactly during a drag. */
  progress: number
}

/**
 * A COMPOSITED slide the chrome can join, rather than follow: the exact spring trajectory the panes were
 * handed to the compositor with. The SlidingMarker uses it to build its own transform animation from the
 * same samples, in the same task — so the underline and the panes share one clock and one commit and cannot
 * disagree, no matter how starved the main thread gets (the per-frame `set()` publishes still flow for the
 * midpoint active-link flip, but nothing visual has to ride them frame by frame). Published by the scroll
 * engine's link glide; ended on a catch (the finger takes over → the marker falls back to `set()`), and
 * cleared with the slide.
 */
export interface ProgressGlide {
  from: string
  to: string
  /** Spring progress per sample, evenly spaced across `durationMs` (see springSamples). */
  samples: number[]
  durationMs: number
}

/**
 * The CONTINUOUS position of the running slide — the same number the panes are drawn from, published so chrome
 * can be glued to the motion rather than merely told about it afterwards. The tab underline rides this: drag a
 * tab a third of the way across and the underline sits a third of the way between the two tabs.
 *
 * Deliberately NOT a React hook feeding component state: this updates every animation frame, and re-rendering a
 * tab bar 60 times a second to move one underline is exactly the kind of cost this primitive keeps off the
 * critical path. Subscribers read it in an effect and write the DOM directly (see sliding-marker.tsx).
 */
// ONE object, mutated in place, rather than a fresh one every animation frame — a slide ticks this 60 times a
// second and the garbage was showing up in the profile. Readers consume it synchronously inside their
// notification and must not hold onto the reference.
const live: TransitionProgress = { from: '', to: '', progress: 0 }
let progress: TransitionProgress | null = null
let glide: ProgressGlide | null = null
const progressListeners = new Set<() => void>()

// A slide ticks this every animation frame, but the only subscriber (the underline in sliding-marker.tsx)
// interpolates its pixel position across the inter-tab distance — so a progress delta this far below one whole
// step of travel can't move the marker even a subpixel, and waking the subscriber for it is pure wasted work.
// 0.001 of travel is <1px on any realistic tab bar; the endpoints and a from/to change ALWAYS publish (below),
// so the marker still lands exactly and re-seats when the slide's group changes.
const PROGRESS_NOTIFY_EPSILON = 0.001
// The last progress we actually notified on — compared against, rather than `live.progress`, so a run of
// sub-epsilon steps accumulates and still fires once it has summed past the threshold (a slow drag can't stall).
let lastNotified = -1

export const transitionProgress = {
  set(from: string, to: string, value: number): void {
    const groupChanged = from !== live.from || to !== live.to
    live.from = from
    live.to = to
    live.progress = value
    progress = live
    // Endpoints must land exactly (the marker's resting seat); a group change must re-seat immediately.
    const settled = value === 0 || value === 1
    if (!settled && !groupChanged && Math.abs(value - lastNotified) < PROGRESS_NOTIFY_EPSILON) return
    lastNotified = value
    for (const l of progressListeners) l()
  },
  clear(): void {
    if (progress === null) return // already idle — don't wake every subscriber for a no-op
    // Same trap as navVisualPath.set(null): clearing a slide that is visibly mid-flight makes the marker
    // re-seat under the committed destination for a frame. See VISUAL-PATH-SUSPECT above.
    if (import.meta.env.DEV && live.progress > 0.02 && live.progress < 0.98) {
      gtlog('PROGRESS-CLEAR-SUSPECT', {
        from: live.from,
        to: live.to,
        at: Math.round(live.progress * 100) / 100,
        stack: new Error('trace').stack?.split('\n').slice(2, 6).join(' | '),
      })
    }
    progress = null
    glide = null
    lastNotified = -1
    for (const l of progressListeners) l()
  },
  /** Announce a composited slide the chrome can join (see ProgressGlide). */
  startGlide(g: ProgressGlide): void {
    glide = g
    for (const l of progressListeners) l()
  },
  /** The composited slide ended early (a finger caught it): fall back to per-frame `set()` publishes. */
  endGlide(): void {
    if (glide === null) return
    glide = null
    for (const l of progressListeners) l()
  },
  getGlide: (): ProgressGlide | null => glide,
  get: (): TransitionProgress | null => progress,
  subscribe(l: () => void): () => void {
    progressListeners.add(l)
    return () => {
      progressListeners.delete(l)
    }
  },
}
